import { useState } from 'react';
import { canIndependentManageShop } from '../../../helpers/businesses';
import useUser from '../../../hooks/global/useUser';
import { LoadedUser } from '../../../interfaces';
import SettingsSection from '../SettingsSection';
import FormSmallModal, { FormData } from '../../global/FormSmallModal';
import {
  updateMinorSettingsSchema,
  UpdateMinorSettingsPayload,
} from '../../../types/businesses';
import useUpdateBusiness from '../../../hooks/businesses/useUpdateBusiness';
import { isUserAdminOrManager } from '../../../utils/global/helper';

const TOOLTIP = {
  minimumTattooAgeInYears:
    "Anyone below 18 will be treated as a minor. Use this field to set the minimum age that you'll tattoo (i.e., 16+)",
};

function MinorSettings() {
  const [form, setForm] = useState<FormData | null>(null);

  const { user } = useUser<LoadedUser>();
  const { mutateAsync: updateBusiness } = useUpdateBusiness({
    onSuccess: () => {
      setForm(null);
    },
  });

  if (!user?.business) return null;

  const { business } = user;

  const canManageMinorSettings =
    isUserAdminOrManager(user) || canIndependentManageShop(user);

  if (!canManageMinorSettings) return null;

  const minorSettingsForm: FormData<UpdateMinorSettingsPayload> = {
    title: 'Minor (under 18) Tattoo Settings',
    initialValues: {
      allowMinors: business.allowMinors ?? false,
      minimumTattooAgeInYears: business.minimumTattooAgeInYears ?? 14,
      requireMinorIdentification: business.requireMinorIdentification ?? true,
      requireMinorBirthCertificate:
        business.requireMinorBirthCertificate ?? true,
      requireMinorGuardianIdentification:
        business.requireMinorGuardianIdentification ?? true,
      requireMinorGuardianPresent: business.requireMinorGuardianPresent ?? true,
    },
    validationSchema: updateMinorSettingsSchema,
    onSubmit: async values => {
      await updateBusiness({
        allowMinors: values.allowMinors,
        minimumTattooAgeInYears: values.minimumTattooAgeInYears,
        requireMinorIdentification: values.requireMinorIdentification,
        requireMinorBirthCertificate: values.requireMinorBirthCertificate,
        requireMinorGuardianIdentification:
          values.requireMinorGuardianIdentification,
        requireMinorGuardianPresent: values.requireMinorGuardianPresent,
      });
    },
    inputRows: values => [
      [
        {
          inputType: 'select',
          label: 'Allow minors?',
          name: 'allowMinors',
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
          autoWidth: false,
        },
      ],
      [
        {
          inputType: 'number',
          label: 'Minimum age',
          name: 'minimumTattooAgeInYears',
          placeholder: '14',
          tooltip: TOOLTIP.minimumTattooAgeInYears,
          min: 0,
          max: 17,
          suffix: 'years',
          isVisible: values.allowMinors,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Require minor ID upload',
          name: 'requireMinorIdentification',
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
          autoWidth: false,
          isVisible: values.allowMinors,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Require minor birth certificate upload',
          name: 'requireMinorBirthCertificate',
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
          autoWidth: false,
          isVisible: values.allowMinors,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Parent/Guardian must be present',
          name: 'requireMinorGuardianPresent',
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
          autoWidth: false,
          isVisible: values.allowMinors,
        },
      ],
      [
        {
          inputType: 'select',
          label: 'Require Parent/Guardian ID upload',
          name: 'requireMinorGuardianIdentification',
          options: [
            { value: true, label: 'Yes' },
            { value: false, label: 'No' },
          ],
          autoWidth: false,
          isVisible: values.allowMinors,
        },
      ],
    ],
  };

  return (
    <>
      <SettingsSection
        title="Minor (under 18) Tattoo Settings"
        subtitle="Update the settings below to control how you handle minor (under 18) tattoos, if they're allowed, minimum age, and policy requirements."
        rows={[
          {
            title: 'Allow minors?',
            label: business.allowMinors ?? false ? 'Yes' : 'No',
          },
          ...(business.allowMinors
            ? [
                {
                  title: 'Minimum age',
                  label: `${business.minimumTattooAgeInYears} years`,
                  tooltip: TOOLTIP.minimumTattooAgeInYears,
                },
                {
                  title: 'Require Minor ID Upload',
                  label: business.requireMinorIdentification ? 'Yes' : 'No',
                },
                {
                  title: 'Require Minor Birth Certificate Upload',
                  label: business.requireMinorBirthCertificate ? 'Yes' : 'No',
                },
                {
                  title: 'Parent/Guardian must be present',
                  label: business.requireMinorGuardianPresent ? 'Yes' : 'No',
                },
                {
                  title: 'Require Parent/Guardian ID Upload',
                  label: business.requireMinorGuardianIdentification
                    ? 'Yes'
                    : 'No',
                },
              ]
            : []),
        ]}
        action={{
          type: 'button',
          text: 'Edit',
          onClick: () => setForm(minorSettingsForm),
        }}
      />
      <FormSmallModal form={form} onClose={() => setForm(null)} open={!!form} />
    </>
  );
}

export default MinorSettings;
